import React, { useState, useEffect } from 'react';
import '../styles.css';

// Toast 通知組件
const Toast = ({ message, type = 'info', duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300); // 等待動畫完成
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#4CAF50',
          color: 'white',
          borderLeft: '4px solid #2e7d32'
        };
      case 'error':
        return {
          backgroundColor: '#f44336',
          color: 'white',
          borderLeft: '4px solid #c62828'
        };
      case 'warning':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
          borderLeft: '4px solid #f57c00'
        };
      case 'info':
      default:
        return {
          backgroundColor: '#2196F3',
          color: 'white',
          borderLeft: '4px solid #1565c0'
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="toast"
      style={{
        ...getTypeStyles(),
        position: 'fixed',
        top: '20px',
        right: '20px',
        padding: '1rem 1.5rem',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10000,
        minWidth: '300px',
        maxWidth: '500px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        animation: 'slideInRight 0.3s ease-out',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease-out'
      }}
    >
      <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{getIcon()}</span>
      <div style={{ flex: 1, wordBreak: 'break-word' }}>{message}</div>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onClose?.(), 300);
        }}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '1.25rem',
          cursor: 'pointer',
          padding: '0',
          marginLeft: '0.5rem',
          opacity: 0.8
        }}
      >
        ×
      </button>
    </div>
  );
};

// Toast 容器組件（管理多個 Toast）
export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // 將 addToast 方法掛載到 window 上，方便全局調用
  useEffect(() => {
    window.showToast = addToast;
    return () => {
      delete window.showToast;
    };
  }, []);

  return (
    <div style={{ 
      position: 'fixed', 
      top: '20px', 
      right: '20px', 
      zIndex: 10000,
      pointerEvents: 'none'
    }}>
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            marginBottom: index > 0 ? '0.5rem' : 0,
            pointerEvents: 'auto'
          }}
        >
          <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

// Hook 用於在組件中使用 Toast
export const useToast = () => {
  const addToast = (message, type = 'info', duration = 3000) => {
    if (window.showToast) {
      return window.showToast(message, type, duration);
    }
    // 如果 ToastContainer 還沒掛載，使用 alert 作為後備
    alert(message);
  };

  return {
    showSuccess: (message, duration) => addToast(message, 'success', duration),
    showError: (message, duration) => addToast(message, 'error', duration),
    showWarning: (message, duration) => addToast(message, 'warning', duration),
    showInfo: (message, duration) => addToast(message, 'info', duration)
  };
};

export default Toast;
